import { ActionIcon, Tooltip } from '@mantine/core';
import { variants } from '@uag/react-core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';
import { useShallow } from 'zustand/shallow';

import { useInputCaptureHandler } from 'app/modules/wallforming/components/input-binding/hooks/useInputCaptureHandler';
import { InputBindingDialog } from 'app/modules/wallforming/components/input-binding/InputBindingDialog';
import { InputBindingFooter } from 'app/modules/wallforming/components/input-binding/InputBindingFooter';
import { InputBindingProfileBar } from 'app/modules/wallforming/components/input-binding/InputBindingProfileBar';
import {
    BINDING_GRID_COLUMNS,
    InputBindingRow,
    ReservedInputRow
} from 'app/modules/wallforming/components/input-binding/InputBindingRow';
import { InputContextFilter } from 'app/modules/wallforming/components/input-binding/InputContextFilter';
import { InputStateMenu } from 'app/modules/wallforming/components/input-binding/InputStateMenu';
import { PointerBindingTable } from 'app/modules/wallforming/components/input-binding/PointerBindingTable';
import {
    INPUT_BINDING_SLOTS,
    inputActionIds,
    inputActions,
    reservedInputs
} from 'app/modules/wallforming/components/input-binding/registry/inputActionsRegistry';
import { InputCategory } from 'app/modules/wallforming/components/input-binding/types/inputCategory';
import { InputDevice } from 'app/modules/wallforming/components/input-binding/types/inputDevice';
import { isContextActive } from 'app/modules/wallforming/components/input-binding/utils/inputContextUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { InputActionDefinition } from 'app/modules/wallforming/components/input-binding/types/inputActionDefinition';

const PANEL_WIDTH = 900;
const PANEL_HEIGHT = 600;
const PANEL_MARGIN = 24;
const isLargeScreen = window.screen.width >= 1920;

const stopPropagation = (event: { stopPropagation: () => void }) => event.stopPropagation();

export const InputBindingPanel = () => {
    const { t } = useTranslation();

    const { isOpen, setIsOpen, category, contextFilter } = usePlacementStore(
        useShallow((state) => ({
            isOpen: state.isInputBindingPanelOpen,
            setIsOpen: state.setIsInputBindingPanelOpen,
            category: state.selectedInputCategory,
            contextFilter: state.inputContextFilter
        }))
    );

    useInputCaptureHandler();

    const panel = useMemo(() => {
        const width = Math.min(PANEL_WIDTH, window.innerWidth - PANEL_MARGIN);
        const height = Math.min(PANEL_HEIGHT, window.innerHeight - PANEL_MARGIN);
        return { width, height, x: (window.innerWidth - width) / 2, y: (window.innerHeight - height) / 2 };
    }, []);

    const { keyboardActionIds, pointerActionIds, reserved } = useMemo(() => {
        const isVisible = (entry: Pick<InputActionDefinition, 'category' | 'contexts'>) =>
            entry.category === category && isContextActive(entry.contexts, contextFilter);
        const visibleActionIds = inputActionIds.filter((actionId) => isVisible(inputActions[actionId]));
        return {
            keyboardActionIds: visibleActionIds.filter((actionId) =>
                inputActions[actionId].devices.includes(InputDevice.Keyboard)
            ),
            pointerActionIds: visibleActionIds.filter(
                (actionId) => !inputActions[actionId].devices.includes(InputDevice.Keyboard)
            ),
            reserved: reservedInputs.filter(isVisible)
        };
    }, [category, contextFilter]);

    return (
        <>
            <Tooltip
                disabled={isOpen}
                label={t('labelInputBindings')}
                position="right"
                variant={variants.tooltip.right}
                zIndex={20000}
            >
                <ActionIcon
                    aria-pressed={isOpen}
                    className={`bg-background-action-hover hover:bg-background-gray! pointer-events-auto ${isLargeScreen ? 'action-icon-large' : 'action-icon-medium'}`}
                    variant={isOpen ? variants.actionIcon.activated : 'default'}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    keyboard
                </ActionIcon>
            </Tooltip>

            {isOpen && (
                <div className="pointer-events-none fixed inset-0" style={{ zIndex: 10000 }}>
                    <Rnd
                        bounds="window"
                        default={panel}
                        dragHandleClassName="rnd-drag-handle"
                        enableResizing={false}
                        enableUserSelectHack={false}
                    >
                        <div
                            className="pointer-events-auto relative flex flex-col overflow-hidden rounded-lg border border-border-ultra-light bg-background-default text-xs text-text-default shadow-xl select-none"
                            style={{ width: panel.width, height: panel.height }}
                            data-input-binding-panel
                        >
                            <header className="rnd-drag-handle flex cursor-move items-center justify-between border-b border-border-ultra-light bg-background-gray-ultra-light px-3 py-1.5">
                                <span className="text-[13px] font-semibold tracking-wide">
                                    {t('labelInputBindings')}
                                </span>
                                <ActionIcon
                                    className="action-icon-small icon-sm"
                                    variant="subtle"
                                    onClick={() => setIsOpen(false)}
                                    onMouseDown={stopPropagation}
                                >
                                    close
                                </ActionIcon>
                            </header>

                            <InputBindingProfileBar />

                            <div className="flex min-h-0 flex-1">
                                <InputStateMenu />

                                <main className="flex min-w-0 flex-1 flex-col">
                                    <InputContextFilter />
                                    <div className="min-h-0 flex-1 space-y-0.5 overflow-auto py-1">
                                        {keyboardActionIds.length > 0 && (
                                            <>
                                                <div
                                                    className="grid items-center gap-x-2 border-b border-border-ultra-light bg-background-gray-ultra-ultra-light px-4 py-1.5 font-semibold uppercase text-text-secondary"
                                                    style={{ gridTemplateColumns: BINDING_GRID_COLUMNS }}
                                                >
                                                    <span>{t('labelCommand')}</span>
                                                    {Array.from({ length: INPUT_BINDING_SLOTS }, (_, slot) => (
                                                        <span key={slot} className="text-center">
                                                            {t('labelKeyBindingSlot', { index: slot + 1 })}
                                                        </span>
                                                    ))}
                                                </div>
                                                {keyboardActionIds.map((actionId) => (
                                                    <InputBindingRow key={actionId} actionId={actionId} />
                                                ))}
                                            </>
                                        )}
                                        {reserved.map((input) => (
                                            <ReservedInputRow key={input.id} input={input} />
                                        ))}
                                        <PointerBindingTable
                                            actionIds={pointerActionIds}
                                            showClickType={category === InputCategory.Poi}
                                        />
                                    </div>
                                </main>
                            </div>

                            <InputBindingFooter onClose={() => setIsOpen(false)} />
                            <InputBindingDialog />
                        </div>
                    </Rnd>
                </div>
            )}
        </>
    );
};
